import React, { useState } from "react";
import { useCentralStore } from "../../store/Central";
import { useTabConfigStore } from "../../store/TabConfig/tab-config.store";
import { Button } from "../../components/ui/button";
import { parseDensity, DENSITY_PRESETS } from "../../config/density-config";
import { DensitySettings, TabConfig } from "../../types/tab-config.types";
import { Slider } from "../../components/ui/slider";
import {
	Plus,
	PanelLeftClose,
	PanelLeft,
	Edit2,
	Check,
	XCircle,
	Loader2,
} from "lucide-react";
import { TabConfigEditor } from "./components/tab-config-editor.component";
import { ConfigActions } from "./components/config-actions.component";
import { ConfigurableJobInfoTabs } from "../job-page/job-page-components/job-info-tabs.component";
import { useJobStore } from "../../store/Jobs";
import { Input } from "../../components/ui/input";
import { TooltipWrapper } from "../../components/ui/tooltip-wrapper";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export const JobTabsConfigurationPage: React.FC = () => {
	const organizationId = useCentralStore((state) => state.organization?.id);
	const {
		configurations,
		selectedConfigId,
		defaultConfigId,
		showSidebar,
		createConfiguration,
		duplicateConfiguration,
		deleteConfiguration,
		selectConfiguration,
		setAsDefault,
		toggleSidebar,
	} = useTabConfigStore();

	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [configToDelete, setConfigToDelete] = useState<{
		id: string;
		name: string;
	} | null>(null);
	const autosaveStatus = useTabConfigStore((state) => state.autosaveStatus);
	const [isEditingName, setIsEditingName] = useState(false);
	const [editingName, setEditingName] = useState("");
	const [viewMode, setViewMode] = useState<"edit" | "preview">("edit");

	const selectedConfig = configurations.find(
		(c) => c.id === selectedConfigId
	);
	const { renameConfiguration, updateDensity } = useTabConfigStore();

	const handleDeleteClick = (config: { id: string; name: string }) => {
		setConfigToDelete(config);
		setShowDeleteDialog(true);
	};

	const confirmDelete = () => {
		if (configToDelete) {
			deleteConfiguration(configToDelete.id);
			setConfigToDelete(null);
		}
		setShowDeleteDialog(false);
	};

	const handleStartEditingName = () => {
		if (selectedConfig) {
			setIsEditingName(true);
			setEditingName(selectedConfig.name);
		}
	};

	const handleSaveName = async () => {
		if (
			selectedConfig &&
			editingName.trim() &&
			editingName !== selectedConfig.name
		) {
			await renameConfiguration(selectedConfig.id, editingName.trim());
		}
		setIsEditingName(false);
	};

	const handleCancelEditingName = () => {
		if (selectedConfig) {
			setEditingName(selectedConfig.name);
		}
		setIsEditingName(false);
	};

	return (
		<>
			<div className="h-full flex flex-col">
				<div className="grid grid-cols-12 flex-1 overflow-hidden">
					{/* Configurations Section - Left sidebar */}
					{showSidebar && (
						<div className="col-span-2 bg-white border-r border-gray-200 flex flex-col min-h-0 overflow-auto">
							<div className="p-4 border-b border-gray-200 flex justify-between items-center">
								<h2 className="text-lg font-semibold text-gray-900">
									Ansichten
								</h2>
								<Button
									size="sm"
									variant="outline"
									className="h-7 px-2 flex items-center gap-1"
									onClick={() => {
										if (organizationId) {
											createConfiguration(
												organizationId,
												`Konfiguration ${
													configurations.length + 1
												}`
											);
										}
									}}
								>
									<Plus className="h-3 w-3" />
									<span className="text-xs">
										Neue Ansicht
									</span>
								</Button>
							</div>
							<div className="flex-1 overflow-auto p-2">
								{configurations.length === 0 ? (
									<div className="text-center py-8">
										<p className="text-gray-500 text-sm">
											Keine Konfigurationen vorhanden
										</p>
									</div>
								) : (
									<div className="space-y-2">
										{configurations.map((config) => (
											<div
												key={config.id}
												className={`p-3 rounded-lg cursor-pointer transition-colors group ${
													selectedConfigId ===
													config.id
														? "bg-blue-50 border border-blue-300"
														: "hover:bg-gray-50 border border-transparent"
												}`}
												onClick={() =>
													selectConfiguration(
														config.id
													)
												}
											>
												<div className="flex justify-between items-center">
													<div className="flex items-center gap-2">
														<span className="font-medium text-sm">
															{config.name}
														</span>
													</div>
													<ConfigActions
														configId={config.id}
														isDefault={
															defaultConfigId ===
															config.id
														}
														variant="compact"
														onToggleDefault={() =>
															setAsDefault(
																defaultConfigId ===
																	config.id
																	? null
																	: config.id
															)
														}
														onDuplicate={() =>
															duplicateConfiguration(
																config.id
															)
														}
														onDelete={() =>
															handleDeleteClick(
																config
															)
														}
													/>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{/* Configuration Editor - Main content area */}
					<div
						className={`${
							showSidebar ? "col-span-10" : "col-span-12"
						} bg-white flex flex-col flex-1 overflow-hidden`}
					>
						{selectedConfig ? (
							<>
								<div className="p-4 border-b border-gray-200 flex items-center justify-between">
									<div className="flex items-center gap-2">
										<TooltipWrapper
											title={
												showSidebar
													? "Ansichten ausblenden"
													: "Ansichten anzeigen"
											}
										>
											<Button
												variant="ghost"
												size="icon"
												onClick={toggleSidebar}
												className="h-8 w-8"
												aria-label={
													showSidebar
														? "Ansichten ausblenden"
														: "Ansichten anzeigen"
												}
											>
												{showSidebar ? (
													<PanelLeftClose className="h-4 w-4" />
												) : (
													<PanelLeft className="h-4 w-4" />
												)}
											</Button>
										</TooltipWrapper>
										{isEditingName ? (
											<>
												<Input
													value={editingName}
													onChange={(e) =>
														setEditingName(
															e.target.value
														)
													}
													onKeyDown={(e) => {
														if (e.key === "Enter")
															handleSaveName();
														if (e.key === "Escape")
															handleCancelEditingName();
													}}
													className="h-8 w-64 font-semibold text-base"
													autoFocus
												/>
												<TooltipWrapper title="Speichern">
													<Button
														size="sm"
														variant="ghost"
														className="h-8 w-8 p-0"
														onClick={handleSaveName}
														aria-label="Speichern"
													>
														<Check className="h-4 w-4" />
													</Button>
												</TooltipWrapper>
												<TooltipWrapper title="Abbrechen">
													<Button
														size="sm"
														variant="ghost"
														className="h-8 w-8 p-0"
														onClick={
															handleCancelEditingName
														}
														aria-label="Abbrechen"
													>
														<XCircle className="h-4 w-4" />
													</Button>
												</TooltipWrapper>
											</>
										) : (
											<>
												<h2 className="text-lg font-semibold text-gray-900">
													{selectedConfig.name}
												</h2>
												<TooltipWrapper title="Ansicht umbenennen">
													<Button
														size="sm"
														variant="ghost"
														className="h-8 w-8 p-0"
														onClick={
															handleStartEditingName
														}
														aria-label="Ansicht umbenennen"
													>
														<Edit2 className="h-4 w-4" />
													</Button>
												</TooltipWrapper>
												<div className="ml-2 pl-2 border-l border-gray-200">
													<ConfigActions
														configId={
															selectedConfig.id
														}
														isDefault={
															defaultConfigId ===
															selectedConfig.id
														}
														variant="full"
														onToggleDefault={() =>
															setAsDefault(
																defaultConfigId ===
																	selectedConfig.id
																	? null
																	: selectedConfig.id
															)
														}
														onDuplicate={() =>
															duplicateConfiguration(
																selectedConfig.id
															)
														}
														onDelete={() =>
															handleDeleteClick({
																id: selectedConfig.id,
																name: selectedConfig.name,
															})
														}
													/>
												</div>
											</>
										)}
									</div>
									<div className="flex items-center gap-4">
										<div className="flex items-center gap-2 text-sm text-gray-400">
											{autosaveStatus.saving ? (
												<>
													<Loader2 className="h-3.5 w-3.5 animate-spin" />
													<span>Speichern...</span>
												</>
											) : autosaveStatus.hasUnsavedChanges ? (
												<span className="text-amber-500">
													Ungespeicherte Änderungen
												</span>
											) : autosaveStatus.lastSavedAt ? (
												<>
													<Check className="h-3.5 w-3.5 text-green-500" />
													<span>
														Zuletzt gespeichert um{" "}
														{autosaveStatus.lastSavedAt.toLocaleTimeString(
															"de-CH",
															{
																hour: "2-digit",
																minute: "2-digit",
															}
														)}
													</span>
												</>
											) : null}
										</div>
									</div>
									<div className="flex items-center border-l border-gray-200 pl-4 gap-1">
										<Button
											variant={
												viewMode === "edit"
													? "default"
													: "ghost"
											}
											size="sm"
											className="h-7 px-2 text-xs"
											onClick={() => setViewMode("edit")}
										>
											Bearbeiten
										</Button>
										<Button
											variant={
												viewMode === "preview"
													? "default"
													: "ghost"
											}
											size="sm"
											className="h-7 px-2 text-xs"
											onClick={() =>
												setViewMode("preview")
											}
										>
											Vorschau
										</Button>
									</div>
								</div>
								<div className="flex-1 overflow-auto">
									{viewMode === "preview" && (
										<DensityPreview
											config={selectedConfig}
											onUpdate={updateDensity}
										/>
									)}
									<div
										className={
											viewMode === "preview"
												? "hidden"
												: "p-6"
										}
									>
										<TabConfigEditor
											key={selectedConfig.id}
											config={selectedConfig}
										/>
									</div>
								</div>
							</>
						) : (
							<>
								<div className="p-4 border-b border-gray-200 flex items-center gap-2">
									<TooltipWrapper
										title={
											showSidebar
												? "Ansichten ausblenden"
												: "Ansichten anzeigen"
										}
									>
										<Button
											variant="ghost"
											size="icon"
											onClick={toggleSidebar}
											className="h-8 w-8"
											aria-label={
												showSidebar
													? "Ansichten ausblenden"
													: "Ansichten anzeigen"
											}
										>
											{showSidebar ? (
												<PanelLeftClose className="h-4 w-4" />
											) : (
												<PanelLeft className="h-4 w-4" />
											)}
										</Button>
									</TooltipWrapper>
									<h2 className="text-lg font-semibold text-gray-900">
										Auftragsansichten
									</h2>
								</div>
								<div className="flex-1 flex items-center justify-center">
									<div className="text-center">
										<p className="text-gray-500">
											Wählen Sie eine Konfiguration aus
										</p>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>

			<AlertDialog
				open={showDeleteDialog}
				onOpenChange={setShowDeleteDialog}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Konfiguration löschen
						</AlertDialogTitle>
						<AlertDialogDescription>
							Möchten Sie die Konfiguration "
							{configToDelete?.name}" wirklich löschen? Diese
							Aktion kann nicht rückgängig gemacht werden.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction onClick={confirmDelete}>
							Löschen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

const DensityPreview: React.FC<{
	config: {
		id: string;
		tabs?: unknown;
		density_padding?: number;
		density_gap?: number;
		density_input_height?: number;
		density_font_size?: number;
	};
	onUpdate: (id: string, density: DensitySettings) => void;
}> = ({ config, onUpdate }) => {
	const current = parseDensity(config);
	const [local, setLocal] = useState<DensitySettings>(current);
	const tabs = (config.tabs ?? []) as TabConfig[];

	React.useEffect(() => {
		setLocal(parseDensity(config));
	}, [config.id]);

	React.useEffect(() => {
		const store = useJobStore.getState();
		const hadJob = !!store.job;
		if (!hadJob) {
			useJobStore.setState({
				job: {
					id: "preview",
					patient_id: "preview-patient",
					practitioner_id: "preview-practitioner",
					client_id: "preview-client",
					organization_id: "preview-org",
					description: "Vollkeramik Krone 15",
					tooth_color: "A3",
					incoming_order_date: "2026-04-01",
					outgoing_order_date: "2026-04-08",
					tp_value: 1.0,
				} as any,
				formFieldsPatient: {
					title: "Frau",
					first_name: "Sarah",
					last_name: "Meier",
					code: "P00028",
					birth_date: "1985-03-15",
					street: "Bahnhofstrasse 12",
					city: "Zürich",
					postal_code: "8001",
					country: "CH",
					gender: "female",
					ahv_number: "756.1234.5678.90",
					additional_information: "",
				} as any,
				formFieldsPractitioner: {
					title: "Dr.",
					first_name: "Thomas",
					last_name: "Müller",
					email: "mueller@praxis.ch",
				} as any,
				formFieldsJob: {
					description: "Vollkeramik Krone 15",
					tooth_color: "A3",
					tax_rate: "8.1",
					prod_country_other: "",
					insurance: "CSS Krankenversicherung",
				} as any,
			});
		}
		return () => {
			if (!hadJob) {
				useJobStore.setState({
					job: null,
					formFieldsPatient: {} as any,
					formFieldsPractitioner: {} as any,
					formFieldsJob: {} as any,
				});
			}
		};
	}, []);

	const handleChange = (key: keyof DensitySettings, value: number) => {
		const updated = { ...local, [key]: value };
		setLocal(updated);
		onUpdate(config.id as string, updated);
	};

	const applyPreset = (name: string) => {
		const preset = DENSITY_PRESETS[name];
		setLocal(preset);
		onUpdate(config.id as string, preset);
	};

	return (
		<div className="flex flex-col h-full">
			{/* Sliders bar */}
			<div className="px-6 py-3 flex items-center gap-6 bg-gray-50 border-b border-gray-200 shrink-0 flex-wrap">
				<div className="flex gap-1.5">
					{Object.keys(DENSITY_PRESETS).map((name) => (
						<Button
							key={name}
							variant="outline"
							size="sm"
							className="h-7 px-3 text-xs"
							onClick={() => applyPreset(name)}
						>
							{name === "big"
								? "Gross"
								: name === "medium"
									? "Mittel"
									: "Klein"}
						</Button>
					))}
				</div>
				<div className="flex items-center gap-2 flex-1 min-w-[160px]">
					<span className="text-xs text-gray-500 shrink-0">Rand</span>
					<Slider
						value={[local.padding]}
						onValueChange={([v]) => handleChange("padding", v)}
						min={0}
						max={24}
						step={2}
						className="flex-1"
					/>
					<span className="text-xs font-mono w-8 text-right text-gray-600">
						{local.padding}px
					</span>
				</div>
				<div className="flex items-center gap-2 flex-1 min-w-[160px]">
					<span className="text-xs text-gray-500 shrink-0">
						Abstand
					</span>
					<Slider
						value={[local.gap]}
						onValueChange={([v]) => handleChange("gap", v)}
						min={0}
						max={20}
						step={2}
						className="flex-1"
					/>
					<span className="text-xs font-mono w-8 text-right text-gray-600">
						{local.gap}px
					</span>
				</div>
				<div className="flex items-center gap-2 flex-1 min-w-[160px]">
					<span className="text-xs text-gray-500 shrink-0">
						Feldhöhe
					</span>
					<Slider
						value={[local.inputHeight]}
						onValueChange={([v]) => handleChange("inputHeight", v)}
						min={24}
						max={56}
						step={4}
						className="flex-1"
					/>
					<span className="text-xs font-mono w-8 text-right text-gray-600">
						{local.inputHeight}px
					</span>
				</div>
				<div className="flex items-center gap-2 flex-1 min-w-[160px]">
					<span className="text-xs text-gray-500 shrink-0">
						Schrift
					</span>
					<Slider
						value={[local.fontSize ?? 14]}
						onValueChange={([v]) => handleChange("fontSize", v)}
						min={10}
						max={18}
						step={1}
						className="flex-1"
					/>
					<span className="text-xs font-mono w-8 text-right text-gray-600">
						{local.fontSize ?? 14}px
					</span>
				</div>
			</div>

			{/* Real component preview */}
			<div className="flex-1 min-h-0 overflow-auto bg-white">
				<ConfigurableJobInfoTabs
					showAdHocTab={false}
					key={config.id}
					tabConfig={tabs}
					densitySettings={local}
				/>
			</div>
		</div>
	);
};
